import { useEffect } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { cn } from '@lyearn/style';
import { BaseButton, Typography } from '@lyearn/ui';
import Loader from '@/components/Loader';

import StatusMenu from '../../features/actionItems/components/ActionStatus/StatusMenu';
import { StatusFragment } from '../../views/TaskStasusesManager/graphql/fragments/generated/Status';
import useStatuses from '../../views/TaskStasusesManager/hooks/useStatuses';
import getTaskStatusIcon from '../../views/TaskStasusesManager/utils/getStatusIcon';
import { baseButtonFocusStyles, TASK_PILL_VARIANT_CONFIG, TaskPillVariant } from './styles';

interface Props {
  statusId?: string | null;
  className?: string;
  editable?: boolean;
  onChange: (statusInfo: StatusFragment, id?: string, isSideEffect?: boolean) => void;
  variant: TaskPillVariant;
  statusInfo?: StatusFragment | null;
}

const TaskStatus = (props: Props) => {
  const { className, variant, editable, statusId, statusInfo: statusInfoProp, onChange } = props;

  const { statuses, isFetching } = useStatuses({
    excludeEntityStatuses: true,
    statusIds: statusId ? [statusId] : undefined,
    pause: !!statusInfoProp,
  });

  const [isOpen, { toggle }] = useToggle(false);

  const statusInfo = statusInfoProp ?? statuses?.[0];

  const variantConfig = TASK_PILL_VARIANT_CONFIG[variant];

  // On creating new task when we don't send statusId, set 1st status from statuses
  useEffect(() => {
    if (!statusId && statusInfo) {
      onChange(statusInfo, undefined, true);
    }
  }, [onChange, statusInfo, statusId]);

  const renderTaskStatus = () => (
    <BaseButton
      aria-label="status"
      className={cn(
        'w-max p-0 space-s-8',
        baseButtonFocusStyles,
        variantConfig.root.className,
        className,
        { [variantConfig.rootEditable.className]: editable },
      )}
      disabled={!editable}>
      {isFetching || !statusInfo ? (
        <Loader className="flex-grow-0" showLabel={false} size={18} />
      ) : (
        <>
          {getTaskStatusIcon({
            category: statusInfo.category,
            icon: statusInfo.icon,
            size: 18,
            progress: statusInfo.progress,
          })}
          <Typography
            className={variantConfig.label.className}
            color={variantConfig.label.color}
            variant="body-short-02">
            {statusInfo.name}
          </Typography>
        </>
      )}
    </BaseButton>
  );

  if (editable) {
    return (
      <StatusMenu
        open={isOpen}
        popoverPosition={variantConfig.popoverPosition}
        statusInfo={statusInfo}
        onOpenChange={toggle}
        onStatusChange={onChange}>
        {renderTaskStatus()}
      </StatusMenu>
    );
  }

  return renderTaskStatus();
};

export default TaskStatus;
